// src/pages/RelatorioPerformance.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  TextField, Button, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem,
  Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, Collapse, IconButton,
  Tooltip
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  Timer as TimerIcon,
  Speed as SpeedIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  SwapHoriz as SwapHorizIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';

const formatarTempo = (minutos) => {
  if (minutos === null || minutos === undefined || minutos < 0) return '-';
  if (minutos < 1) return `${Math.round(minutos * 60)}s`;
  if (minutos < 60) return `${Math.round(minutos)}min`;
  const horas = Math.floor(minutos / 60);
  const mins = Math.round(minutos % 60);
  if (horas < 24) return mins > 0 ? `${horas}h ${mins}min` : `${horas}h`;
  const dias = Math.floor(horas / 24);
  const hrs = horas % 24;
  return `${dias}d ${hrs}h ${mins}min`;
};

const formatarDataHora = (isoString) => {
  if (!isoString) return '-';
  const d = new Date(isoString);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getTempoColor = (minutos, media) => {
  if (!media || media <= 0) return 'default';
  if (minutos <= media * 0.7) return 'success';
  if (minutos <= media * 1.3) return 'warning';
  return 'error';
};

// Ordem real do fluxo produtivo
const ORDEM_FLUXO = [
  'Farmaceutica',
  'Ordem de produção',
  'Pesagem inicial',
  'Pesagem final',
  'Encapsulação',
  'Dermato',
  'Xarope',
  'Sachê',
  'Peso Médio',
  'Conferência',
  'Saida LAB',
  'Logistica',
  'Expedição'
];

// Setores descontinuados - ignorar nas transições
const SETORES_IGNORAR = ['Pesagem'];

const getOrdemSetor = (nomeSetor) => {
  if (!nomeSetor) return 999;
  const nome = nomeSetor.toLowerCase().trim();
  // Match exato primeiro para evitar "Pesagem" casar com "Pesagem inicial"
  const idxExato = ORDEM_FLUXO.findIndex(s => s.toLowerCase() === nome);
  if (idxExato >= 0) return idxExato;
  // Match parcial
  const idx = ORDEM_FLUXO.findIndex(s =>
    nome.includes(s.toLowerCase()) || s.toLowerCase().includes(nome)
  );
  return idx >= 0 ? idx : 999;
};

const ordenarPorFluxo = (lista, campoSetor = 'setor') => {
  return [...lista].sort((a, b) => {
    const ordemA = getOrdemSetor(typeof a === 'string' ? a : a[campoSetor]);
    const ordemB = getOrdemSetor(typeof b === 'string' ? b : b[campoSetor]);
    return ordemA - ordemB;
  });
};

// Transições adjacentes válidas no fluxo produtivo
// Encapsulação, Dermato, Xarope e Sachê são paralelos (mesma etapa, formas diferentes)
const TRANSICOES_ADJACENTES = [
  ['Farmaceutica', 'Ordem de produção'],
  ['Farmaceutica', 'Pesagem inicial'],
  ['Ordem de produção', 'Pesagem inicial'],
  ['Pesagem inicial', 'Pesagem final'],
  ['Pesagem final', 'Encapsulação'],
  ['Pesagem final', 'Dermato'],
  ['Pesagem final', 'Xarope'],
  ['Pesagem final', 'Sachê'],
  ['Encapsulação', 'Peso Médio'],
  ['Dermato', 'Peso Médio'],
  ['Xarope', 'Peso Médio'],
  ['Sachê', 'Peso Médio'],
  ['Peso Médio', 'Conferência'],
  ['Conferência', 'Saida LAB'],
  ['Saida LAB', 'Logistica'],
  ['Logistica', 'Expedição']
];

const filtrarTransicoesValidas = (transicoes) => {
  return transicoes.filter(t => {
    const partes = (t.transicao || '').split(' → ');
    if (partes.length < 2) return false;
    const origem = partes[0].trim().toLowerCase();
    const destino = partes[1].trim().toLowerCase();
    return TRANSICOES_ADJACENTES.some(([a, b]) =>
      origem.includes(a.toLowerCase()) && destino.includes(b.toLowerCase()) ||
      a.toLowerCase().includes(origem) && b.toLowerCase().includes(destino)
    );
  }).sort((a, b) => {
    const partesA = (a.transicao || '').split(' → ');
    const partesB = (b.transicao || '').split(' → ');
    const ordemA = getOrdemSetor(partesA[0]) * 100 + getOrdemSetor(partesA[1]);
    const ordemB = getOrdemSetor(partesB[0]) * 100 + getOrdemSetor(partesB[1]);
    return ordemA - ordemB;
  });
};

const RelatorioPerformance = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dados, setDados] = useState(null);
  const [tabAtiva, setTabAtiva] = useState(0);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    setor: ''
  });
  const [setores, setSetores] = useState([]);
  const [expandidas, setExpandidas] = useState({});
  const [dadosEvolucao, setDadosEvolucao] = useState(null);
  const [loadingEvolucao, setLoadingEvolucao] = useState(false);

  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

    setFiltros(prev => ({
      ...prev,
      dataInicio: primeiroDia.toISOString().split('T')[0],
      dataFim: ultimoDia.toISOString().split('T')[0]
    }));

    carregarSetores();
  }, []);

  const carregarSetores = async () => {
    try {
      const response = await api.listarSetores();
      if (response.success) {
        setSetores(response.dados || []);
      }
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  };

  const carregarDados = async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o periodo (data inicio e fim)');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const parametros = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim
      };
      if (filtros.setor) parametros.setor = filtros.setor;

      const res = await api.relatorioPerformanceTempo(parametros);

      if (res.success) {
        setDados(res.dados);
      } else {
        setError('Erro ao carregar dados');
      }
    } catch (error) {
      console.error('Erro ao carregar relatorio:', error);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const carregarEvolucao = async () => {
    try {
      setLoadingEvolucao(true);
      const res = await api.relatorioEvolucaoMensal({});
      if (res.success) {
        setDadosEvolucao(res.dados);
      }
    } catch (error) {
      console.error('Erro ao carregar evolucao:', error);
    } finally {
      setLoadingEvolucao(false);
    }
  };

  // Carregar evolução quando mudar para as abas de evolução
  useEffect(() => {
    if ((tabAtiva === 4 || tabAtiva === 5) && !dadosEvolucao && !loadingEvolucao) {
      carregarEvolucao();
    }
  }, [tabAtiva]);

  const toggleExpandir = (codigo) => {
    setExpandidas(prev => ({ ...prev, [codigo]: !prev[codigo] }));
  };

  const setoresOrdenados = useMemo(() => {
    if (!dados?.por_setor) return [];
    const filtrados = dados.por_setor.filter(s =>
      !SETORES_IGNORAR.includes(s.setor) && getOrdemSetor(s.setor) !== 999
    );
    return ordenarPorFluxo(filtrados);
  }, [dados?.por_setor]);

  const transicoesValidas = useMemo(() => {
    if (!dados?.transicoes) return [];
    return filtrarTransicoesValidas(dados.transicoes);
  }, [dados?.transicoes]);

  const funcionariosOrdenados = useMemo(() => {
    if (!dados?.por_funcionario) return [];
    return dados.por_funcionario;
  }, [dados?.por_funcionario]);

  const mediaGeralFunc = useMemo(() => {
    if (!funcionariosOrdenados.length) return 0;
    return funcionariosOrdenados.reduce((s, f) => s + f.tempo_medio_min, 0) / funcionariosOrdenados.length;
  }, [funcionariosOrdenados]);

  const chartDataSetor = useMemo(() => {
    if (!dadosEvolucao?.por_setor) return [];
    return ordenarPorFluxo(dadosEvolucao.por_setor).map(s => ({
      setor: s.setor,
      [dadosEvolucao.periodo.mes_anterior.nome]: s.mes_anterior?.requisicoes || 0,
      [dadosEvolucao.periodo.mes_atual.nome]: s.mes_atual?.requisicoes || 0,
      variacao: s.variacao_percentual
    }));
  }, [dadosEvolucao]);

  const chartDataFuncionario = useMemo(() => {
    if (!dadosEvolucao?.por_funcionario) return [];
    return dadosEvolucao.por_funcionario
      .filter(f => (f.mes_atual?.requisicoes || 0) > 0 || (f.mes_anterior?.requisicoes || 0) > 0)
      .slice(0, 30)
      .map(f => ({
        nome: (f.nome || '').split(' ').slice(0, 2).join(' '),
        nomeCompleto: f.nome,
        setor: f.setor,
        [dadosEvolucao.periodo.mes_anterior.nome]: f.mes_anterior?.requisicoes || 0,
        [dadosEvolucao.periodo.mes_atual.nome]: f.mes_atual?.requisicoes || 0,
        variacao: f.variacao_percentual
      }));
  }, [dadosEvolucao]);

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{
        p: 3, mb: 3,
        background: 'linear-gradient(135deg, #9c27b0 0%, #673ab7 100%)',
        color: 'white',
        borderRadius: 2
      }}>
        <Box display="flex" alignItems="center">
          <AnalyticsIcon sx={{ mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Relatorio de Performance Produtiva
            </Typography>
            <Typography variant="subtitle1" opacity={0.9}>
              Analise de tempo de producao por requisicao, setor e funcionario
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filtros</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Data Inicio"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Setor (Opcional)</InputLabel>
              <Select
                value={filtros.setor}
                onChange={(e) => setFiltros(prev => ({ ...prev, setor: e.target.value }))}
                label="Setor (Opcional)"
              >
                <MenuItem value="">Todos os Setores</MenuItem>
                {setores.map(setor => (
                  <MenuItem key={setor.id} value={setor.nome}>{setor.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={carregarDados}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
            >
              {loading ? 'Carregando...' : 'Gerar Analise de Performance'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Tabs - sempre visíveis */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabAtiva} onChange={(_, v) => setTabAtiva(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<SpeedIcon />} label="Por Setor" iconPosition="start" />
          <Tab icon={<SwapHorizIcon />} label="Transicoes" iconPosition="start" />
          <Tab icon={<PersonIcon />} label="Por Funcionario" iconPosition="start" />
          <Tab icon={<AccessTimeIcon />} label="Requisicoes Detalhadas" iconPosition="start" />
          <Tab icon={<TimelineIcon />} label="Evolucao Mensal Setor" iconPosition="start" />
          <Tab icon={<TrendingUpIcon />} label="Evolucao Mensal Funcionario" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Resultados de Performance */}
      {dados && tabAtiva <= 3 && (
        <>
          {/* Cards resumo */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TimerIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {formatarTempo(dados.resumo_geral?.tempo_medio_total_min)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Tempo Medio Total</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AssessmentIcon sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {dados.resumo_geral?.total_requisicoes || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Requisicoes Analisadas</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <SpeedIcon sx={{ fontSize: 36, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {dados.resumo_geral?.total_setores_envolvidos || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Setores Envolvidos</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 36, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {dados.resumo_geral?.total_funcionarios_envolvidos || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Funcionarios Envolvidos</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tab: Por Setor */}
          {tabAtiva === 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tempo Medio por Setor
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Quanto tempo cada setor leva para entregar a requisicao ao proximo setor (ordenado pelo fluxo produtivo)
              </Typography>
              {setoresOrdenados.length > 0 ? (
                <Grid container spacing={2}>
                  {setoresOrdenados.map((setor, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={setor.setor}>
                      <Card elevation={2} sx={{
                        borderLeft: 4,
                        borderColor: 'primary.main'
                      }}>
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Chip label={idx + 1} size="small" color="primary" />
                            <Typography variant="h6" fontWeight="bold">
                              {setor.setor}
                            </Typography>
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Tempo Medio</Typography>
                              <Typography variant="h5" fontWeight="bold" color="primary.main">
                                {formatarTempo(setor.tempo_medio_min)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Mediana</Typography>
                              <Typography variant="h5" fontWeight="bold" color="secondary.main">
                                {formatarTempo(setor.tempo_mediano_min)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Minimo</Typography>
                              <Typography variant="body1" color="success.main">
                                {formatarTempo(setor.tempo_minimo_min)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Maximo</Typography>
                              <Typography variant="body1" color="error.main">
                                {formatarTempo(setor.tempo_maximo_min)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Chip
                                label={`${setor.total_requisicoes} requisicoes`}
                                size="small"
                                color="default"
                                sx={{ mt: 1 }}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="text.secondary">Nenhum dado de setor encontrado</Typography>
              )}
            </Paper>
          )}

          {/* Tab: Transicoes */}
          {tabAtiva === 1 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tempo de Transicao entre Setores
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Quanto tempo leva para a requisicao passar de um setor para o proximo
              </Typography>
              {transicoesValidas.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Transicao</strong></TableCell>
                        <TableCell align="center"><strong>Qtd</strong></TableCell>
                        <TableCell align="center"><strong>Tempo Medio</strong></TableCell>
                        <TableCell align="center"><strong>Minimo</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transicoesValidas.map((t, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={(t.transicao || '').split(' → ')[0] || 'N/A'}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <ArrowForwardIcon fontSize="small" color="action" />
                              <Chip
                                label={(t.transicao || '').split(' → ')[1] || 'N/A'}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="center">{t.total}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={formatarTempo(t.tempo_medio_min)}
                              color={t.tempo_medio_min > 60 ? 'error' : t.tempo_medio_min > 30 ? 'warning' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">{formatarTempo(t.tempo_minimo_min)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">Nenhuma transicao entre setores encontrada</Typography>
              )}
            </Paper>
          )}

          {/* Tab: Por Funcionario */}
          {tabAtiva === 2 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Performance por Funcionario
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tempo medio entre a bipagem do funcionario e a proxima etapa (agrupado por setor)
              </Typography>
              {funcionariosOrdenados.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>Funcionario</strong></TableCell>
                        <TableCell><strong>Setor</strong></TableCell>
                        <TableCell align="center"><strong>Requisicoes</strong></TableCell>
                        <TableCell align="center"><strong>Tempo Medio</strong></TableCell>
                        <TableCell align="center"><strong>Mediana</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {funcionariosOrdenados.map((func, idx) => {
                        const color = getTempoColor(func.tempo_medio_min, mediaGeralFunc);
                        return (
                          <TableRow key={`${func.codigo_funcionario}-${func.setor}`} hover>
                            <TableCell>
                              <Chip
                                label={idx + 1}
                                size="small"
                                color={idx < 3 ? 'success' : 'default'}
                                variant={idx < 3 ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={idx < 3 ? 'bold' : 'normal'}>
                                {func.nome || 'Sem nome'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={func.setor} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">{func.total_requisicoes}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatarTempo(func.tempo_medio_min)}
                                color={color}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">{formatarTempo(func.tempo_mediano_min)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">Nenhum dado de funcionario encontrado</Typography>
              )}
            </Paper>
          )}

          {/* Tab: Requisicoes Detalhadas */}
          {tabAtiva === 3 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Requisicoes Detalhadas (Top 100 mais lentas)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Detalhamento do fluxo de cada requisicao entre setores
              </Typography>
              {dados.requisicoes_detalhadas?.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={40} />
                        <TableCell><strong>Requisicao</strong></TableCell>
                        <TableCell align="center"><strong>Tempo Total</strong></TableCell>
                        <TableCell align="center"><strong>Etapas</strong></TableCell>
                        <TableCell align="center"><strong>Primeira Leitura</strong></TableCell>
                        <TableCell align="center"><strong>Ultima Leitura</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dados.requisicoes_detalhadas.map((req) => (
                        <React.Fragment key={req.codigo_requisicao}>
                          <TableRow
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleExpandir(req.codigo_requisicao)}
                          >
                            <TableCell>
                              <IconButton size="small">
                                {expandidas[req.codigo_requisicao] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="bold">{req.codigo_requisicao}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatarTempo(req.tempo_total_min)}
                                color={req.tempo_total_min > (dados.resumo_geral?.tempo_medio_total_min * 1.5) ? 'error' : req.tempo_total_min > dados.resumo_geral?.tempo_medio_total_min ? 'warning' : 'success'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">{req.total_etapas}</TableCell>
                            <TableCell align="center">{formatarDataHora(req.primeira_data)}</TableCell>
                            <TableCell align="center">{formatarDataHora(req.ultima_data)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ p: 0 }} colSpan={6}>

                              <Collapse in={expandidas[req.codigo_requisicao]} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                  <Typography variant="subtitle2" gutterBottom>Fluxo da Requisicao:</Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell><strong>Setor</strong></TableCell>
                                        <TableCell><strong>Funcionario</strong></TableCell>
                                        <TableCell align="center"><strong>Data Bipagem</strong></TableCell>
                                        <TableCell align="center"><strong>Proximo Setor</strong></TableCell>
                                        <TableCell align="center"><strong>Tempo ate Proximo</strong></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {req.etapas.map((etapa, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell>
                                            <Chip label={etapa.setor} size="small" color="primary" variant="outlined" />
                                          </TableCell>
                                          <TableCell>{etapa.funcionario || '-'}</TableCell>
                                          <TableCell align="center">{formatarDataHora(etapa.data)}</TableCell>
                                          <TableCell align="center">
                                            {etapa.prox_setor ? (
                                              <Chip label={etapa.prox_setor} size="small" color="secondary" variant="outlined" />
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">Fim</Typography>
                                            )}
                                          </TableCell>
                                          <TableCell align="center">
                                            {etapa.tempo_ate_proximo_min !== null ? (
                                              <Tooltip title="Tempo ate o proximo setor">
                                                <Chip
                                                  icon={<ArrowForwardIcon />}
                                                  label={formatarTempo(etapa.tempo_ate_proximo_min)}
                                                  size="small"
                                                  color={etapa.tempo_ate_proximo_min > 60 ? 'error' : etapa.tempo_ate_proximo_min > 30 ? 'warning' : 'success'}
                                                />
                                              </Tooltip>
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">-</Typography>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">Nenhuma requisicao detalhada encontrada</Typography>
              )}
            </Paper>
          )}
        </>
      )}

      {/* Tab: Evolução Mensal Setor */}
      {tabAtiva === 4 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Evolucao Mensal por Setor
          </Typography>
          {loadingEvolucao ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : dadosEvolucao ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Comparativo: {dadosEvolucao.periodo.mes_anterior.nome} vs {dadosEvolucao.periodo.mes_atual.nome}
              </Typography>

              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartDataSetor} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="setor" angle={-35} textAnchor="end" height={80} fontSize={12} />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey={dadosEvolucao.periodo.mes_anterior.nome} fill="#90a4ae" name={dadosEvolucao.periodo.mes_anterior.nome} />
                  <Bar dataKey={dadosEvolucao.periodo.mes_atual.nome} fill="#7c4dff" name={dadosEvolucao.periodo.mes_atual.nome} />
                </BarChart>
              </ResponsiveContainer>

              <TableContainer sx={{ mt: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Setor</strong></TableCell>
                      <TableCell align="center"><strong>{dadosEvolucao.periodo.mes_anterior.nome}</strong></TableCell>
                      <TableCell align="center"><strong>{dadosEvolucao.periodo.mes_atual.nome}</strong></TableCell>
                      <TableCell align="center"><strong>Variacao</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chartDataSetor.map((s) => (
                      <TableRow key={s.setor} hover>
                        <TableCell><strong>{s.setor}</strong></TableCell>
                        <TableCell align="center">{s[dadosEvolucao.periodo.mes_anterior.nome]}</TableCell>
                        <TableCell align="center">{s[dadosEvolucao.periodo.mes_atual.nome]}</TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={s.variacao > 0 ? <ArrowUpwardIcon /> : s.variacao < 0 ? <ArrowDownwardIcon /> : null}
                            label={`${s.variacao > 0 ? '+' : ''}${s.variacao}%`}
                            size="small"
                            color={s.variacao > 0 ? 'success' : s.variacao < 0 ? 'error' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography color="text.secondary">Carregando dados...</Typography>
          )}
        </Paper>
      )}

      {/* Tab: Evolução Mensal Funcionário */}
      {tabAtiva === 5 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Evolucao Mensal por Funcionario
          </Typography>
          {loadingEvolucao ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : dadosEvolucao ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Top 30 funcionarios - {dadosEvolucao.periodo.mes_anterior.nome} vs {dadosEvolucao.periodo.mes_atual.nome}
              </Typography>

              <ResponsiveContainer width="100%" height={500}>
                <BarChart data={chartDataFuncionario} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nome" angle={-45} textAnchor="end" height={100} fontSize={11} />
                  <YAxis />
                  <RechartsTooltip
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0]) return `${payload[0].payload.nomeCompleto} (${payload[0].payload.setor})`;
                      return label;
                    }}
                  />
                  <Legend />
                  <Bar dataKey={dadosEvolucao.periodo.mes_anterior.nome} fill="#90a4ae" name={dadosEvolucao.periodo.mes_anterior.nome} />
                  <Bar dataKey={dadosEvolucao.periodo.mes_atual.nome} fill="#7c4dff" name={dadosEvolucao.periodo.mes_atual.nome} />
                </BarChart>
              </ResponsiveContainer>

              <TableContainer sx={{ mt: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>#</strong></TableCell>
                      <TableCell><strong>Funcionario</strong></TableCell>
                      <TableCell><strong>Setor</strong></TableCell>
                      <TableCell align="center"><strong>{dadosEvolucao.periodo.mes_anterior.nome}</strong></TableCell>
                      <TableCell align="center"><strong>{dadosEvolucao.periodo.mes_atual.nome}</strong></TableCell>
                      <TableCell align="center"><strong>Variacao</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chartDataFuncionario.map((f, idx) => (
                      <TableRow key={`${f.nomeCompleto}-${f.setor}`} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell><strong>{f.nomeCompleto}</strong></TableCell>
                        <TableCell>
                          <Chip label={f.setor} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">{f[dadosEvolucao.periodo.mes_anterior.nome]}</TableCell>
                        <TableCell align="center">{f[dadosEvolucao.periodo.mes_atual.nome]}</TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={f.variacao > 0 ? <ArrowUpwardIcon /> : f.variacao < 0 ? <ArrowDownwardIcon /> : null}
                            label={`${f.variacao > 0 ? '+' : ''}${f.variacao}%`}
                            size="small"
                            color={f.variacao > 0 ? 'success' : f.variacao < 0 ? 'error' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography color="text.secondary">Carregando dados...</Typography>
          )}
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Analisando performance...</Typography>
        </Box>
      )}

      {/* Empty State */}
      {!loading && !dados && !error && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <AnalyticsIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhum dado encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ajuste os filtros e tente novamente
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioPerformance;
